import { NodeType } from '@amarantos/api-client'

/** `circle` is a plain CSS round div, as before; the other four are drawn as an SVG polygon instead. */
export type NodeSymbolShape = 'circle' | 'square' | 'diamond' | 'hexagon' | 'triangle'

export type CorneredShape = Exclude<NodeSymbolShape, 'circle'>

/** One shape per node type (AMA-120) — a silhouette, not an icon, since the symbol is drawn in canvas
 *  space and fine detail turns to noise at low zoom the same way the chip's text does. */
export const nodeTypeSymbolShape: Record<NodeType, NodeSymbolShape> = {
	[NodeType.manhole]: 'circle',
	[NodeType.tank]: 'square',
	[NodeType.separator]: 'diamond',
	[NodeType.pump_station]: 'hexagon',
	[NodeType.soakaway]: 'triangle',
	// Same circle as a manhole, but hollow/always-dashed/status-blind in `ManholeNode` — an inert node (Tie-in,
	// Inlet) builds nothing (CONTEXT.md), so it reads as "not really a node" instead of earning its own shape.
	[NodeType.tie_in]: 'circle',
	[NodeType.inlet]: 'circle',
}

/**
 * Polygon points for the four cornered shapes, normalised against `NODE_SYMBOL_VIEWBOX`. Every shape is
 * regular (equal sides), which `POLYGON_DASH` relies on to land dashes evenly on every side. The diamond
 * is the square's own points rotated 45° about the centre, so its corners land outside the viewbox. The
 * hexagon and triangle are sized a notch larger than the square — at an equal side length, a regular
 * hexagon/triangle fills less of its bounding box and reads visibly smaller once drawn.
 */
export const NODE_SYMBOL_POLYGON_POINTS: Record<CorneredShape, ReadonlyArray<readonly [number, number]>> = {
	square: [
		[2, 2],
		[38, 2],
		[38, 38],
		[2, 38],
	],
	diamond: [
		[20, -5.46],
		[45.46, 20],
		[20, 45.46],
		[-5.46, 20],
	],
	hexagon: [
		[8.75, 0.51],
		[31.25, 0.51],
		[42.5, 20],
		[31.25, 39.49],
		[8.75, 39.49],
		[-2.5, 20],
	],
	triangle: [
		[-1, 7.88],
		[41, 7.88],
		[20, 44.25],
	],
}

export const NODE_SYMBOL_VIEWBOX = 40

/**
 * Each cornered shape's own dash rhythm — a divisor of that shape's own side length above, sized for
 * ~2–3 dashes per side to visually match the Tie-in's plain CSS dashed circle. `offset` is half the dash
 * length, which centres a dash on distance 0 and therefore on every vertex (a whole multiple of the
 * dash+gap period away), so corners always land on a solid dash instead of a cut.
 */
export const POLYGON_DASH: Record<CorneredShape, { array: readonly [number, number]; offset: number }> = {
	square: { array: [7.2, 4.8], offset: 3.6 }, // side 36, 3 dashes/side
	diamond: { array: [7.2, 4.8], offset: 3.6 }, // same side as the square it's rotated from
	hexagon: { array: [6.75, 4.5], offset: 3.375 }, // side 22.5, 2 dashes/side
	triangle: { array: [8.4, 5.6], offset: 4.2 }, // side 42, 3 dashes/side
}

export function scalePolygonPoints(shape: CorneredShape, sizePx: number): string {
	const scale = sizePx / NODE_SYMBOL_VIEWBOX
	return NODE_SYMBOL_POLYGON_POINTS[shape].map(([x, y]) => `${x * scale},${y * scale}`).join(' ')
}

export function scalePolygonDash(shape: CorneredShape, sizePx: number): { dasharray: string; dashoffset: number } {
	const scale = sizePx / NODE_SYMBOL_VIEWBOX
	const { array, offset } = POLYGON_DASH[shape]
	return {
		dasharray: array.map((value) => value * scale).join(' '),
		dashoffset: offset * scale,
	}
}
